// import { Capacitor } from '@capacitor/core'
import { route } from 'quasar/wrappers'
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
} from 'vue-router'
import routes from './routes'
import { Roles } from 'src/common/types'
import { APP_VERSION } from 'src/hooks/public/useSupportedVersionCheck'
import { useDebugStore, useAppStore } from 'src/stores'
import { Capacitor } from '@capacitor/core'
import { useAuthStore } from 'src/common/store'
const HISTORY = 'history'
const debugCode = '1'
const WELCOME_NAME = 'welcome'
const OUTDATED_VERSION_NAME = 'outdatedVersion'
const isCapacitor = Capacitor.isNativePlatform()

const createHistory = process.env.SERVER
  ? createMemoryHistory
  : isCapacitor
    ? createWebHashHistory // ✅ safer for Capacitor
    : process.env.VUE_ROUTER_MODE === HISTORY
      ? createWebHistory
      : createWebHashHistory

export const router = createRouter({
  scrollBehavior: () => ({ left: 0, top: 0 }),
  routes,
  history: createHistory(process.env.VUE_ROUTER_BASE),
})

export default route(() => {
  router.beforeEach(async (to, from, next) => {
    const { debug } = to.query
    const debugStore = useDebugStore()
    const appStore = useAppStore()
    const authStore = useAuthStore()
    const env = process.env.ENV

    if (debug === debugCode && process.env.DEV) {
      debugStore.turnOnDebugMode()
    }

    const minimumSupportedAppVersion = appStore.minimumSupportedAppVersion
    if (minimumSupportedAppVersion) {
      if (
        APP_VERSION < minimumSupportedAppVersion &&
        to.name !== OUTDATED_VERSION_NAME
      ) {
        return next({ name: OUTDATED_VERSION_NAME })
      }
    }

    if (
      to.path.includes('/callback') ||
      to.path.includes('/forms/') ||
      to.path.includes('404') ||
      to.path.includes('/deposit')
    ) {
      return next()
    }

    if (to.name === 'client.password.change') {
      return next()
    }

    if (from.name === 'admin.login' && to.name === 'admin.password.reset') {
      return next()
    }

    // If the user is trying to access the login page or welcome page
    // and they already authenticated, redirect them to their center
    if (
      to.name === 'admin.login' ||
      to.name === 'client.login' ||
      to.name === WELCOME_NAME
    ) {
      if (!authStore.isAuthenticated) {
        return next()
      }
      if (authStore.lastSessionInfo.lastLoginMode === 'client') {
        return next({
          name: 'client.dashboard',
        })
      }
      return next({ name: 'admin.attendance' })
    }

    const {
      roles = [
        Roles.SuperAdmin,
        Roles.OrgAdmin,
        Roles.CenterAdmin,
        Roles.OfficeStaff,
        Roles.RoomStaff,
        Roles.Client,
        Roles.Anonymous,
      ],
      excludeEnv = [],
    } = to.meta
    const userRole = authStore.role ?? Roles.Anonymous

    if (!authStore.isSuperAdmin && excludeEnv.includes(env ?? 'DEV')) {
      return next({ name: WELCOME_NAME })
    }

    //If the user is not authenticated
    if (!authStore.isAuthenticated) {
      if (
        to.name === 'client.password.reset' ||
        to.name === 'admin.password.reset' ||
        to.name === WELCOME_NAME
      ) {
        return next()
      }

      if (!authStore.lastSessionInfo.lastLoginMode) {
        return next({
          name: WELCOME_NAME,
        })
      }

      //if user is client mode
      if (authStore.lastSessionInfo.lastLoginMode === 'client') {
        return next({
          name: 'client.login',
        })
      }
      return next({ name: 'admin.login' })
    }

    // Feature Flag Guard: Check if route requires specific feature flags
    // Only check after user is authenticated (store requires auth)
    // if (featureFlag) {
    //   const globalConfigStore = useGlobalConfigStore()

    //   let isFeatureAllowed = true

    //   if (typeof featureFlag === 'string') {
    //     // Simple case: single flag must be enabled
    //     isFeatureAllowed = globalConfigStore.getFlag(featureFlag)
    //   } else {
    //     // Complex case: multiple flags with specific required values
    //     isFeatureAllowed = Object.entries(featureFlag).every(
    //       ([key, requiredValue]) => {
    //         const flagValue = globalConfigStore.getFlag(key)
    //         // Compare: getFlag returns boolean, requiredValue should be boolean
    //         // If requiredValue is true, flag must be true
    //         // If requiredValue is false, flag must be false
    //         return flagValue === Boolean(requiredValue)
    //       }
    //     )
    //   }

    //   if (!isFeatureAllowed) {
    //     // Feature is not enabled, redirect based on user type
    //     if (authStore.isClient) {
    //       return next({ name: 'client.dashboard' })
    //     } else {
    //       return next({ name: 'admin.attendance' })
    //     }
    //   }
    // }

    if (authStore.isClient) {
      // Onboarding/authorization redirects are handled by ClientLayout.vue
      // Just ensure client users stay within client routes
      if (to.name?.toString().startsWith('client.') !== true) {
        return next({ name: 'client.dashboard' })
      }
      return next()
    } else {
      // If the user is trying to access a route that requires a specific role
      if (userRole !== Roles.SuperAdmin && !roles.includes(userRole)) {
        return next({ name: 'admin.attendance' })
      }
      return next()
    }
  })

  return router
})
